import React from "react";
import { Box, Button, Grid, Paper, Skeleton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedIcon from "@mui/icons-material/Verified";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";

/**
 * The Compilations hero — title, the two page-level actions and the four
 * aggregate stat tiles.
 *
 * Laid out so it survives a 360px phone: the action buttons drop below the
 * title and go full width (a comfortable touch target) while the tiles are a
 * 2x2 Grid on xs instead of a flex row that would stack into four tall bars.
 */
export default function CompilationsHero({ stats, pendingCount, loading, onRefresh, onCreate }) {
  const tiles = [
    { label: "Compilations", value: stats.total, icon: <VideoLibraryIcon />, color: "#fff" },
    { label: "WGIDs attached", value: stats.total_wgids, icon: <VerifiedIcon />, color: "#86efac" },
    { label: "Brands used", value: stats.brands_used, icon: <BusinessIcon />, color: "#93c5fd" },
    { label: "Awaiting approval", value: pendingCount, icon: <HourglassEmptyIcon />, color: "#fcd34d" },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2.5,
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: { xs: 3, sm: 4 },
        overflow: "hidden",
        position: "relative",
        background: "linear-gradient(135deg, #064e3b 0%, #047857 55%, #10b981 100%)",
        color: "white",
      }}
    >
      <Box sx={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -60, right: 80, width: 150, height: 150, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />

      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "flex-start" },
          gap: { xs: 1.75, md: 2 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={1.25} mb={0.5}>
            <VideoLibraryIcon sx={{ fontSize: { xs: 22, sm: 26, md: 28 }, opacity: 0.9, flexShrink: 0 }} />
            <Typography
              variant="h4"
              fontWeight={700}
              letterSpacing={-0.5}
              sx={{ fontSize: { xs: 21, sm: 27, md: 32 }, lineHeight: 1.2 }}
            >
              Compilations
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.85,
              pl: { xs: 0, sm: 0.5 },
              maxWidth: 580,
              fontSize: { xs: 12.5, sm: 14 },
              display: { xs: "-webkit-box", sm: "block" },
              WebkitLineClamp: { xs: 3, sm: "unset" },
              WebkitBoxOrient: "vertical",
              overflow: { xs: "hidden", sm: "visible" },
            }}
          >
            Assemble a compilation from verified WGIDs and a video of your own. Uploads go
            straight to S3 in parallel parts, stay private while they await approval, and are
            published to their brand path the moment an admin approves.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            flexShrink: 0,
            "& > *": { flex: { xs: "1 1 140px", md: "0 0 auto" }, minHeight: 40 },
          }}
        >
          <Button
            variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading}
            sx={{
              borderRadius: 2, textTransform: "none", color: "white", borderColor: "rgba(255,255,255,0.4)",
              "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.5)", borderColor: "rgba(255,255,255,0.2)" },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained" startIcon={<AddIcon />} onClick={onCreate}
            sx={{
              borderRadius: 2, textTransform: "none", bgcolor: "white", color: "#065f46",
              fontWeight: 700, boxShadow: "none", whiteSpace: "nowrap",
              "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
            }}
          >
            Create&nbsp;Compilation
          </Button>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mt: { xs: 0.5, sm: 1 }, position: "relative" }}>
        {tiles.map(({ label, value, icon, color }) => (
          <Grid item xs={6} md={3} key={label}>
            <Box
              sx={{
                height: "100%",
                px: { xs: 1.25, sm: 2 }, py: 1,
                borderRadius: 2.5,
                bgcolor: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", gap: 1, minWidth: 0,
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: 18, color, flexShrink: 0 } })}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.75, display: "block", lineHeight: 1.15, fontSize: { xs: 10.5, sm: 12 } }}
                >
                  {label}
                </Typography>
                {loading && value == null ? (
                  <Skeleton width={28} height={22} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
                ) : (
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.25} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {value ?? 0}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}
